import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Upload, Sparkles, CheckCircle2, Zap, Flame, Check, FileText } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const OTTO_URL = "https://customer-assets.emergentagent.com/job_bill-inspector-4/artifacts/20tvm87f_8energy%20personaggio%20-%20senza%20sfondo%20con%20fiama.png";
const OTTO_CHAR_URL = "https://static.wixstatic.com/media/709795_dcb4777a39394c9fb86ba6ad9678de09~mv2.png";

// Prezzi fissi 8energy
const PREZZI = {
  luce_spread: "0.013000",
  luce_comm: "102,00",
  gas_spread: "0.110000",
  gas_comm: "102,00",
  data_scadenza: "30 Aprile 2026"
};

// Link documenti
const DOCS = {
  cte_luce: "https://709795ed-3522-4b5b-a519-c233de9f5112.usrfiles.com/ugd/709795_2c985f24aadd44928cba0664efa18300.pdf",
  cte_gas: "https://709795ed-3522-4b5b-a519-c233de9f5112.usrfiles.com/ugd/709795_2c985f24aadd44928cba0664efa18300.pdf",
  condizioni: "https://customer-assets.emergentagent.com/job_f56f21f7-c17f-4649-a9ca-6cb78c28c69d/artifacts/4adzcav1_Condizioni-Generali2722026.pdf"
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Clean Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <a href="https://www.8energy.it" target="_blank" rel="noopener noreferrer">
              <img src={LOGO_URL} alt="8energy" className="h-8" />
            </a>
            <div className="flex items-center gap-6">
              <a 
                href="https://www.8energy.it" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block"
              >
                Sito web
              </a>
              <Link 
                to="/admin/login" 
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block"
              >
                Area Riservata
              </Link>
              <Link to="/auth">
                <Button 
                  className="bg-black text-white hover:bg-gray-800 rounded-full px-6 h-10 text-sm font-medium"
                  data-testid="nav-login-btn"
                >
                  Accedi
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Certificato Gemini AI</span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-gray-900 leading-[1.1]">
                L'energia sotto analisi.
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-gray-500 leading-relaxed max-w-lg">
                Carica la tua bolletta e scopri in 60 secondi se stai pagando il giusto prezzo per luce e gas.
              </p>

              {/* CTA Button */}
              <div className="pt-4">
                <Link to="/auth">
                  <Button 
                    size="lg"
                    className="bg-black text-white hover:bg-gray-800 rounded-full h-14 px-8 text-base font-medium group"
                    data-testid="hero-cta-btn"
                  >
                    <Upload className="w-5 h-5 mr-3" strokeWidth={1.5} />
                    Analizza la tua bolletta
                    <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                  </Button>
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle2 className="w-4 h-4 text-green-500" strokeWidth={1.5} />
                  Gratuito
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle2 className="w-4 h-4 text-green-500" strokeWidth={1.5} />
                  60 secondi
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle2 className="w-4 h-4 text-green-500" strokeWidth={1.5} />
                  Senza impegno
                </div>
              </div>
            </motion.div>

            {/* Right - OTTO Character */}
            <motion.div 
              className="relative flex justify-center lg:justify-end"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Subtle gradient background */}
              <div className="absolute w-80 h-80 bg-gradient-to-br from-cyan-50 to-lime-50 rounded-full blur-3xl opacity-60"></div>
              
              {/* OTTO */}
              <motion.img 
                src={OTTO_URL} 
                alt="OTTO - Assistente 8energy" 
                className="relative z-10 w-72 sm:w-80 lg:w-96 drop-shadow-xl"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />

              {/* Floating result card */}
              <motion.div 
                className="absolute bottom-8 left-0 bg-white rounded-2xl shadow-xl border border-gray-100 p-5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Bolletta bocciata</p>
                    <p className="text-sm text-green-600">Risparmio possibile: €35/mese</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4">
              Come funziona
            </h2>
            <p className="text-lg text-gray-500">
              Tre semplici passaggi per scoprire se stai pagando troppo
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Carica",
                desc: "Carica il PDF della tua bolletta luce o gas",
                icon: (
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                )
              },
              {
                step: "02",
                title: "Analisi",
                desc: "La nostra AI analizza prezzi, consumi e costi nascosti",
                icon: (
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                  </svg>
                )
              },
              {
                step: "03",
                title: "Verdetto",
                desc: "Scopri se la bolletta è promossa o bocciata",
                icon: (
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" strokeWidth={1.2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                  </svg>
                )
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-3xl p-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-xs font-medium text-gray-400 mb-6">{item.step}</div>
                <div className="w-16 h-16 mx-auto mb-6 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-700">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMO PLUS ONLINE - Offerte 8energy */}
      <section className="py-16 px-4 md:px-6 bg-white" data-testid="promo-offers-section">
        <div className="max-w-[1100px] mx-auto flex flex-col items-center">
          {/* Promo Banner */}
          <motion.div 
            className="mb-3"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div 
              className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full font-extrabold text-sm border-2 border-[#ADFF2F]"
              style={{ animation: 'heartbeat 2s infinite' }}
              data-testid="promo-banner"
            >
              <Zap className="w-4 h-4 text-[#006838]" strokeWidth={2} />
              <span>PROMO PLUS ONLINE</span>
              <span className="text-gray-400">•</span>
              <span>Fino al {PREZZI.data_scadenza}</span>
            </div>
          </motion.div>

          {/* Legal Notice */}
          <p className="text-center text-xs text-gray-400 uppercase tracking-wider font-bold mb-10">
            Offerta esclusiva Web • Validazione tramite firma digitale online
          </p>

          {/* Offers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full mb-12">
            {/* Luce Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <OfferCard
                type="luce"
                title="Luce Plus Online"
                subtitle="Luce Domestico"
                formula="PUN"
                spread={PREZZI.luce_spread}
                unit="€/kWh"
                commission={PREZZI.luce_comm}
                features={["Energia 100% Green"]}
                cteLink={DOCS.cte_luce}
                conditionsLink={DOCS.condizioni}
                color="#006838"
              />
            </motion.div>

            {/* Gas Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <OfferCard
                type="gas"
                title="Gas Plus Online"
                subtitle="Gas Domestico"
                formula="PSV"
                spread={PREZZI.gas_spread}
                unit="€/Smc"
                commission={PREZZI.gas_comm}
                features={["Prezzo Trasparente"]}
                cteLink={DOCS.cte_gas}
                conditionsLink={DOCS.condizioni}
                color="#FF6B00"
              />
            </motion.div>
          </div>

          {/* Final CTA Card */}
          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-gray-100 shadow-xl shadow-black/5 relative overflow-hidden">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    Attiva la tua offerta <span className="text-[#006838]">Online.</span>
                  </h2>
                  <p className="text-gray-500 mb-8">
                    Sottoscrivi il contratto in pochi minuti. La validità dell'offerta è garantita solo tramite procedura digitale.
                  </p>
                  
                  <Link to="/switch-guide">
                    <button 
                      className="inline-flex items-center gap-3 bg-[#006838] text-white px-8 py-5 rounded-full font-extrabold text-lg shadow-xl shadow-[#006838]/25 hover:bg-[#ADFF2F] hover:text-[#006838] hover:-translate-y-1 transition-all duration-300"
                      data-testid="activate-offer-btn"
                    >
                      <FileText className="w-5 h-5" strokeWidth={2} />
                      FIRMA E ATTIVA ORA
                    </button>
                  </Link>

                  <p className="text-xs text-gray-400 mt-6">
                    *I prezzi si riferiscono alla componente energia e materia prima. IVA e imposte escluse.
                  </p>
                </div>

                {/* OTTO Character */}
                <motion.img
                  src={OTTO_CHAR_URL}
                  alt="8Energy Assistant"
                  className="w-40 md:w-52 hidden md:block"
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Styles for animations */}
        <style>{`
          @keyframes heartbeat {
            0% { transform: scale(1); box-shadow: 0 0 10px rgba(173, 255, 47, 0.2); }
            50% { transform: scale(1.05); box-shadow: 0 0 25px rgba(173, 255, 47, 0.5); }
            100% { transform: scale(1); box-shadow: 0 0 10px rgba(173, 255, 47, 0.2); }
          }
          @keyframes rotateBorder {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </section>

      {/* Supported Types */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-gray-400 uppercase tracking-wider mb-8">Analizziamo bollette di</p>
          <div className="flex justify-center items-center gap-12">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
                <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </div>
              <span className="text-xl font-medium text-gray-900">Luce</span>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center">
                <svg className="w-7 h-7 text-orange-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z" />
                </svg>
              </div>
              <span className="text-xl font-medium text-gray-900">Gas</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA - WOW Effect */}
      <section className="py-24 px-4 md:px-6 relative overflow-hidden">
        {/* Background glow effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-[#ADFF2F]/20 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-[#006838]/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Card with rotating border */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-[40px] p-[4px] overflow-hidden"
            data-testid="final-cta-card"
          >
            {/* Rotating Border Effect - Multiple layers */}
            <div 
              className="absolute inset-0"
              style={{
                background: 'conic-gradient(from 0deg, transparent, #ADFF2F, #006838, #ADFF2F, transparent 50%)',
                animation: 'rotateBorderSlow 6s linear infinite'
              }}
            />
            <div 
              className="absolute inset-0"
              style={{
                background: 'conic-gradient(from 180deg, transparent, #006838, #ADFF2F, #006838, transparent 50%)',
                animation: 'rotateBorderSlow 6s linear infinite reverse'
              }}
            />
            
            {/* Glow overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#ADFF2F]/10 via-transparent to-[#006838]/10 animate-pulse"></div>
            
            {/* Card Content */}
            <div className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 rounded-[36px] p-10 md:p-16 z-10">
              {/* Inner glow effects */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-gradient-to-b from-[#ADFF2F]/10 to-transparent rounded-t-[36px]"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#006838]/20 rounded-full blur-[80px] pointer-events-none"></div>
              <div className="absolute top-1/4 left-0 w-64 h-64 bg-[#ADFF2F]/10 rounded-full blur-[60px] pointer-events-none"></div>
              
              {/* Floating particles */}
              <div className="absolute inset-0 overflow-hidden rounded-[36px] pointer-events-none">
                <div className="absolute top-10 left-10 w-2 h-2 bg-[#ADFF2F] rounded-full animate-ping opacity-60"></div>
                <div className="absolute top-20 right-20 w-1.5 h-1.5 bg-[#ADFF2F] rounded-full animate-ping opacity-40" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute bottom-16 left-1/4 w-1 h-1 bg-white rounded-full animate-ping opacity-30" style={{ animationDelay: '1s' }}></div>
                <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-[#ADFF2F] rounded-full animate-ping opacity-50" style={{ animationDelay: '1.5s' }}></div>
              </div>
              
              <div className="relative z-10 text-center">
                {/* Badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full mb-8 border border-white/10"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="w-2 h-2 bg-[#ADFF2F] rounded-full animate-pulse shadow-lg shadow-[#ADFF2F]/50"></div>
                  <span className="text-sm font-semibold text-white/90">Analisi AI Gratuita</span>
                </motion.div>
                
                {/* Main Title with gradient */}
                <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  <span className="text-white">Scopri se stai</span>
                  <br />
                  <span className="bg-gradient-to-r from-[#ADFF2F] via-[#90EE90] to-[#ADFF2F] bg-clip-text text-transparent bg-[length:200%_100%] animate-shimmer">
                    pagando troppo
                  </span>
                </h2>
                
                {/* Subtitle */}
                <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-lg mx-auto">
                  Bastano <span className="text-[#ADFF2F] font-semibold">60 secondi</span> per analizzare la tua bolletta e scoprire quanto puoi risparmiare
                </p>
                
                {/* CTA Button with glow */}
                <Link to="/auth">
                  <motion.button 
                    className="group relative inline-flex items-center gap-3 bg-[#ADFF2F] text-[#006838] px-10 py-5 rounded-full font-extrabold text-lg overflow-hidden"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="cta-analyze-btn"
                  >
                    {/* Button glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <div className="absolute inset-0 rounded-full shadow-[0_0_40px_rgba(173,255,47,0.5)] group-hover:shadow-[0_0_60px_rgba(173,255,47,0.7)] transition-shadow"></div>
                    
                    <Zap className="w-6 h-6 relative z-10" strokeWidth={2.5} />
                    <span className="relative z-10">ANALIZZA ORA</span>
                    <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" strokeWidth={2.5} />
                  </motion.button>
                </Link>
                
                {/* Trust badges */}
                <div className="flex flex-wrap justify-center gap-6 mt-10">
                  {[
                    { icon: "✓", text: "100% Gratuito" },
                    { icon: "⚡", text: "60 Secondi" },
                    { icon: "🔒", text: "Dati Sicuri" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-500">
                      <span className="text-[#ADFF2F]">{item.icon}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Additional animations */}
        <style>{`
          @keyframes rotateBorderSlow {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes shimmer {
            0% { background-position: 100% 0; }
            100% { background-position: -100% 0; }
          }
          .animate-shimmer {
            animation: shimmer 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Minimal Footer */}
      <footer className="py-8 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="8energy" className="h-6 opacity-60" />
              <span className="text-sm text-gray-400">© 2026 8energy S.r.l.</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-gray-400">
              <a href="https://www.8energy.it" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                Sito web
              </a>
              <a href="https://www.8energy.it/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                Privacy Policy
              </a>
              <a href="https://www.8energy.it/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                Cookie Policy
              </a>
              <a href="https://www.8energy.it/dichiarazionediaccessibilita" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                Accessibilità
              </a>
            </div>
          </div>
          <p className="text-xs text-gray-300 text-center">
            8energy S.r.l. - P.IVA 07504390480 | REA FI-708168 | PEC: 8energysrl@cndlpec.it | 
            <a href="https://www.8energy.it/dichiarazionediaccessibilita" target="_blank" rel="noopener noreferrer" className="hover:text-gray-500 ml-1">
              Dichiarazione di Accessibilità
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

// Offer Card Component con bordo rotante
function OfferCard({ type, title, subtitle, formula, spread, unit, commission, features, cteLink, conditionsLink, color }) {
  const isLuce = type === "luce";
  
  return (
    <div 
      className="relative rounded-[38px] p-[3px] overflow-hidden hover:-translate-y-2 transition-transform duration-300"
      data-testid={`offer-card-${type}`}
    >
      {/* Rotating Border Effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'conic-gradient(transparent, #ADFF2F, #006838, transparent 30%)',
          animation: 'rotateBorder 4s linear infinite'
        }}
      />
      <div 
        className="absolute inset-0"
        style={{
          background: 'conic-gradient(transparent, #ADFF2F, #006838, transparent 30%)',
          animation: 'rotateBorder 4s linear infinite',
          transform: 'rotate(180deg)'
        }}
      />
      
      {/* Card Content */}
      <div className="relative bg-white rounded-[35px] p-8 md:p-10 z-10 h-full flex flex-col">
        {/* Subtitle */}
        <span 
          className="font-black text-xs uppercase tracking-widest"
          style={{ color }}
        >
          {subtitle}
        </span>
        
        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mt-2 mb-6">{title}</h2>
        
        {/* Price Box */}
        <div className="bg-[#f5f5f7] rounded-2xl p-6 text-center mb-6">
          <span className="text-gray-600">{formula} + </span>
          <span className="text-2xl font-bold text-[#006838]">{spread}</span>
          <span className="text-xl font-bold text-[#006838]"> {unit}*</span>
        </div>
        
        {/* Features */}
        <ul className="space-y-3 mb-8 flex-1">
          <li className="flex items-center gap-3 text-sm text-gray-600">
            <Check className="w-4 h-4 flex-shrink-0" style={{ color }} strokeWidth={2} />
            Comm.ne: €{commission}/anno
          </li>
          {features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3 text-sm text-gray-600">
              <Check className="w-4 h-4 flex-shrink-0" style={{ color }} strokeWidth={2} />
              {feature}
            </li>
          ))}
        </ul>
        
        {/* Document Links */}
        <div className="flex gap-3">
          <a
            href={cteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-gray-100 text-xs font-bold text-gray-700 hover:text-[#006838] hover:border-[#ADFF2F] hover:bg-[#fafff0] transition-all"
          >
            <FileText className="w-3 h-3" strokeWidth={2} />
            CTE {isLuce ? "LUCE" : "GAS"}
          </a>
          <a
            href={conditionsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-gray-100 text-xs font-bold text-gray-700 hover:text-[#006838] hover:border-[#ADFF2F] hover:bg-[#fafff0] transition-all"
          >
            <FileText className="w-3 h-3" strokeWidth={2} />
            CONDIZIONI
          </a>
        </div>
      </div>
    </div>
  );
}
